import { useQuery, useQueries, useMutation, useQueryClient } from '@tanstack/react-query';
import { useRef } from 'react';
import {
  evaluateVideo,
  getEvaluations,
  getEvaluation,
  deleteEvaluation,
  bulkDeleteEvaluations,
  deleteAllEvaluations,
  getMyEvaluations,
  getMyProgress,
} from '@/services/evaluationApi';
import { useAuth } from '@/contexts/AuthContext';
import type { MyEvaluationsParams } from '@/services/evaluationApi';
import type { BulkDeleteResponse, DeleteAllResponse } from '@/types/api';

/**
 * Hook to evaluate a video
 */
export function useEvaluateVideo() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: evaluateVideo,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['evaluations'] });
    },
  });
}

/**
 * Hook to fetch all evaluations.
 * Query key includes user.id to isolate caches when users switch accounts.
 * Query is disabled when unauthenticated (no user.id).
 */
export function useEvaluations() {
  const { user } = useAuth();
  return useQuery({
    queryKey: ['evaluations', user?.id],
    queryFn: getEvaluations,
    enabled: !!user?.id,
  });
}

/**
 * PS-0a coaching polling ceiling — if llm_explanation is still null after
 * this many milliseconds from the first null-result fetch, stop polling and
 * show the "coaching unavailable" state. This guards against the backend
 * silently failing to produce a coaching narrative (allowed by contract).
 */
const COACHING_POLL_CEILING_MS = 2 * 60 * 1_000; // 2 minutes

/**
 * Hook to fetch a single evaluation.
 *
 * PS-0a (score-fast split): when the fetched eval has llm_explanation=null,
 * this hook polls every 2 s until the coaching narrative arrives or the
 * COACHING_POLL_CEILING_MS ceiling is reached. This makes coaching appear
 * live on EvaluationResults without a manual refresh after a score-fast eval.
 *
 * When llm_explanation is non-null on the first load (normal / fast coaching
 * case), no extra polling occurs — there is no skeleton flash.
 *
 * Additional return field:
 * - `coachingTimedOut` — true when polling stopped at the ceiling and
 *   llm_explanation is still null. EvaluationResults shows a subtle
 *   "coaching unavailable" state rather than an endless skeleton.
 */
export function useEvaluationResult(evaluationId: string | undefined) {
  // Track the timestamp when we first observed llm_explanation=null so we can
  // apply the ceiling without depending on dataUpdatedAt (which resets on every
  // successful refetch and would never trigger the ceiling).
  const nullNarrativeFirstSeenAt = useRef<number | null>(null);

  const query = useQuery({
    queryKey: ['evaluations', evaluationId],
    queryFn: () => getEvaluation(evaluationId!),
    enabled: !!evaluationId,
    // Poll while llm_explanation is absent so coaching streams in live.
    // React Query calls this callback after each successful fetch.
    // Returning false stops polling; returning a number sets the next interval.
    refetchInterval: (q) => {
      const data = q.state.data;
      // Not yet loaded or coaching already present → no polling needed.
      if (!data || data.llm_explanation != null) {
        nullNarrativeFirstSeenAt.current = null; // reset if coaching arrives later
        return false;
      }
      // Record when we first saw a null narrative (first-time only).
      if (nullNarrativeFirstSeenAt.current === null) {
        nullNarrativeFirstSeenAt.current = Date.now();
      }
      // Stop at the ceiling to avoid an endless spinner.
      if (Date.now() - nullNarrativeFirstSeenAt.current > COACHING_POLL_CEILING_MS) {
        return false;
      }
      return 2_000; // poll every 2 s while waiting for coaching
    },
  });

  // Expose whether polling has timed out so the UI can show "coaching unavailable"
  // instead of an endless skeleton. Timed-out = we first saw null narrative,
  // the ceiling has elapsed, and it's still null.
  const coachingTimedOut =
    nullNarrativeFirstSeenAt.current !== null &&
    Date.now() - nullNarrativeFirstSeenAt.current > COACHING_POLL_CEILING_MS &&
    query.data?.llm_explanation == null;

  return {
    ...query,
    coachingTimedOut,
  };
}

/**
 * Hook to delete an evaluation
 */
export function useDeleteEvaluation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: deleteEvaluation,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['evaluations'] });
    },
  });
}

/**
 * Hook to bulk delete evaluations
 */
export function useBulkDeleteEvaluations() {
  const queryClient = useQueryClient();

  return useMutation<BulkDeleteResponse, Error, string[]>({
    mutationFn: bulkDeleteEvaluations,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['evaluations'] });
    },
  });
}

/**
 * Hook to delete all evaluations
 */
export function useDeleteAllEvaluations() {
  const queryClient = useQueryClient();

  return useMutation<DeleteAllResponse, Error, void>({
    mutationFn: deleteAllEvaluations,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['evaluations'] });
    },
  });
}

/**
 * Hook to fetch evaluations scoped to the authenticated user.
 * Calls GET /users/me/evaluations (not the global /evaluations endpoint).
 */
export function useMyEvaluations(params?: MyEvaluationsParams) {
  return useQuery({
    queryKey: ['my-evaluations', params],
    queryFn: () => getMyEvaluations(params),
  });
}

/**
 * Hook to fetch full eval details for up to N evaluation IDs in parallel.
 * Uses useQueries so the array length can vary between renders (Rules-of-Hooks safe).
 * Results are keyed identically to useEvaluationResult — React Query deduplicates
 * fetches so subsequent visits are served from cache instantly.
 */
export function useEvaluationDetails(ids: (string | undefined)[]) {
  return useQueries({
    queries: ids.map((id) => ({
      queryKey: ['evaluations', id],
      queryFn: () => getEvaluation(id!),
      enabled: !!id,
    })),
  });
}

/**
 * Hook to fetch per-template progress for the authenticated user.
 * Calls GET /users/me/progress/{template_id}.
 */
export function useMyProgress(templateId: string | undefined) {
  return useQuery({
    queryKey: ['my-progress', templateId],
    queryFn: () => getMyProgress(templateId!),
    enabled: !!templateId,
  });
}
